import { useFrame } from "@react-three/fiber";
import { useRef } from "react";
import { useSettingsStore } from "../app/settingsStore";

/**
 * Frame timing, sampled inside the render loop and published where the DOM can
 * read it.
 *
 * It lives in the Canvas because that is the only place `useFrame` runs, but
 * it deliberately does not render anything: the readout belongs to the DOM
 * HUD, and putting numbers in the 3D scene would mean re-rendering the scene
 * to update them — which would be a frame-time meter that costs frame time.
 *
 * The published value is a rolling mean rather than the last frame. Instant
 * frame time on a tick-driven game is mostly noise: the ticks themselves are
 * spikes, and a number that flickers between 4 and 40 tells you nothing about
 * whether the machine is keeping up.
 */

/** Frames per published sample. About a quarter-second at 60fps. */
const WINDOW = 15;

interface PerfSnapshot {
  /** Mean milliseconds per frame over the last window. */
  readonly ms: number;
  readonly fps: number;
}

let latest: PerfSnapshot = { ms: 0, fps: 0 };
const listeners = new Set<(perf: PerfSnapshot) => void>();

export function subscribePerf(fn: (perf: PerfSnapshot) => void): () => void {
  listeners.add(fn);
  fn(latest);
  return () => {
    listeners.delete(fn);
  };
}

export function PerfProbe() {
  const showPerf = useSettingsStore((s) => s.showPerf);
  const frames = useRef(0);
  const elapsed = useRef(0);

  useFrame((_, delta) => {
    if (!showPerf) return;
    frames.current += 1;
    elapsed.current += delta;
    if (frames.current < WINDOW) return;

    const ms = (elapsed.current / frames.current) * 1000;
    latest = { ms: Math.round(ms * 10) / 10, fps: Math.round(1000 / Math.max(ms, 0.01)) };
    frames.current = 0;
    elapsed.current = 0;
    for (const fn of [...listeners]) fn(latest);
  });

  return null;
}
